import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PaytrieAPI } from '@paytrie/client'

import { ConfirmDialog } from '@/components/Modal/ConfirmDialog'
import { Button } from '@/components/Button/Button'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import {
  buildSchema,
  DEFAULT_VALUES,
  formatOccupationDisplay,
  formatPostalCode,
  type FormValues,
} from './KYCRefreshForm.logic'
import { AddressFields } from './AddressFields'
import { OccupationFields } from './OccupationFields'
import { packOccupation, unpackOccupation } from './occupation'

type CurrentUser = NonNullable<ReturnType<typeof useCurrentUser>['data']>

const toDefaultValues = (user: CurrentUser): FormValues => {
  const occupation = unpackOccupation(user.occupation ?? '')
  return {
    ...DEFAULT_VALUES,
    address1: user.addressLine1 ?? '',
    address2: user.addressLine2 ?? '',
    city: user.city ?? '',
    province: (user.province ?? '').toUpperCase(),
    postal: formatPostalCode(user.postalCode ?? ''),
    ...occupation,
    existingOccupation: formatOccupationDisplay(occupation),
    isEditingOccupation: occupation.employmentStatus === '',
  }
}

const KYCRefreshFormInner = React.memo(function KYCRefreshFormInner({
  user,
}: {
  user: CurrentUser
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const schema = useMemo(() => buildSchema(t), [t])
  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toDefaultValues(user),
    mode: 'onTouched',
  })
  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
  } = methods

  const isEditingOccupation = useWatch({ control, name: 'isEditingOccupation' })
  const existingOccupation = useWatch({ control, name: 'existingOccupation' })

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const onValid = useCallback(
    async (values: FormValues) => {
      clearErrors('root')

      const provinceCode =
        values.province.toLowerCase() as PaytrieAPI.UpdateCurrentUserBodyProvince
      const postalCode = values.postal.replace(/\s/g, '').toLowerCase()

      try {
        const res = await PaytrieAPI.updateCurrentUser({
          addressLine1: values.address1,
          addressLine2: values.address2 || null,
          city: values.city,
          province: provinceCode,
          postalCode,
          occupation: packOccupation(values),
          refreshKyc: true,
        })

        if (res.status !== 200) {
          setIsConfirmOpen(false)
          setError('root', { message: t('occupation.error.unableToProcess') })
          return
        }

        await queryClient.invalidateQueries({
          queryKey: ['v2', 'users', 'me'],
        })
        navigate('/')
      } catch {
        setIsConfirmOpen(false)
        setError('root', { message: t('occupation.error.unableToProcess') })
      }
    },
    [clearErrors, setError, navigate, queryClient, t]
  )

  return (
    <FormProvider {...methods}>
      <form
        className="grid grid-cols space-y-4"
        onSubmit={handleSubmit(() => setIsConfirmOpen(true))}
      >
        <AddressFields secondaryColumns={4} />

        {!isEditingOccupation && existingOccupation.length > 0 ? (
          <>
            <div
              className="text-lg font-normal uppercase mt-4"
              data-testid="container-status-label"
            >
              {t('occupation.occupation.title')}
            </div>
            <div className="flex items-center justify-between border-b-2 border-b-rock pb-2">
              <div
                className="text-pebble dark:text-snow"
                data-testid="text-current-occupation"
              >
                {existingOccupation}
              </div>
              <Button
                type="button"
                color="primary"
                purpose="change-occupation"
                className="h-10 px-4 rounded-none bg-black text-white dark:bg-white dark:text-black"
                onClick={() =>
                  setValue('isEditingOccupation', true, {
                    shouldValidate: true,
                  })
                }
                data-testid="btn-change-occupation"
              >
                {t('occupation.change')}
              </Button>
            </div>
          </>
        ) : (
          <OccupationFields />
        )}

        <div className="mt-2">
          <ConfirmDialog
            open={isConfirmOpen}
            onOpenChange={setIsConfirmOpen}
            onSubmit={handleSubmit(onValid)}
            disabled={!isValid || isSubmitting}
            triggerLabel={t('kycRefresh.confirm.trigger')}
            title={t('kycRefresh.confirm.title')}
            description={t('kycRefresh.confirm.description')}
            confirmLabel={t('kycRefresh.confirm.submit')}
          />
          {errors.root?.message ? (
            <div
              className="text-sm text-red-600 mt-2"
              role="alert"
              data-testid="text-submit-error"
            >
              {errors.root.message}
            </div>
          ) : null}
        </div>
      </form>
    </FormProvider>
  )
})

type KYCRefreshFormProps = {
  authToken: string
}

const KYCRefreshFormComponent = ({ authToken }: KYCRefreshFormProps) => {
  const { t } = useTranslation()

  const { data: user, isLoading, isError } = useCurrentUser(!!authToken)

  if (isLoading) {
    return (
      <div
        className="text-center text-pebble dark:text-snow py-8"
        data-testid="kyc-refresh-loading"
      >
        {t('personalInfo.loading')}
      </div>
    )
  }

  if (isError || !user) {
    return (
      <div
        className="text-center text-red-600 py-8"
        role="alert"
        data-testid="kyc-refresh-load-error"
      >
        {t('occupation.error.unableToProcess')}
      </div>
    )
  }

  return <KYCRefreshFormInner user={user} />
}

export const KYCRefreshForm = React.memo(KYCRefreshFormComponent)
